import { useState, useRef, useEffect, useMemo, type DragEvent, type ChangeEvent } from "react";
import {
  UploadCloud,
  Eye,
  FileText,
  FileSpreadsheet,
  FileImage,
  FileArchive,
  FileType,
  File as FileIcon,
  Trash2,
  AlertCircle,
  CheckCircle2,
} from "lucide-react";
import { cn } from "@/shared/lib/cn";
import { formatFileSize } from "@/shared/lib/format";
import { IconButton } from "./IconButton";
import { Button } from "./Button";

export interface UploadedFileItem {
  id: string;
  name: string;
  size: number;
  type?: string;
  url?: string;
  status?: "pending" | "uploading" | "success" | "error";
  progress?: number;
  error?: string;
  rawFile?: File;
}

export interface FileUploadProps {
  files?: UploadedFileItem[];
  onChange?: (files: UploadedFileItem[]) => void;
  /** Callback when user drops or selects new raw files */
  onAddFiles?: (files: File[]) => void;
  /** Callback when user deletes a file item */
  onRemoveFile?: (file: UploadedFileItem) => void;
  /** Opens a detail view for one queued file. */
  onFileClick?: (file: UploadedFileItem) => void;
  /** Show this many rows, then scroll the rest inside the list. */
  visibleRows?: number;
  accept?: string;
  multiple?: boolean;
  maxSizeBytes?: number;
  maxCount?: number;
  disabled?: boolean;
  className?: string;
  title?: string;
  hint?: string;
  error?: string;
}

export { formatFileSize };

export function fileTypeIcon(filename: string) {
  const ext = filename.split(".").pop()?.toLowerCase() || "";
  if (["pdf"].includes(ext)) {
    return <FileText className="size-5 text-danger shrink-0" />;
  }
  if (["doc", "docx", "odt", "rtf"].includes(ext)) {
    return <FileType className="size-5 text-info shrink-0" />;
  }
  if (["xls", "xlsx", "csv"].includes(ext)) {
    return <FileSpreadsheet className="size-5 text-success shrink-0" />;
  }
  if (["jpg", "jpeg", "png", "webp", "gif", "svg", "bmp"].includes(ext)) {
    return <FileImage className="size-5 text-primary shrink-0" />;
  }
  if (["zip", "rar", "7z", "tar", "gz"].includes(ext)) {
    return <FileArchive className="size-5 text-warning shrink-0" />;
  }
  return <FileIcon className="size-5 text-text-muted shrink-0" />;
}

function UploadedFileLead({ file }: { file: UploadedFileItem }) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [imgError, setImgError] = useState(false);

  const isImage = useMemo(() => {
    const type = (file.type ?? file.rawFile?.type ?? "").toLowerCase();
    const name = file.name.toLowerCase();
    return type.startsWith("image/") || /\.(png|jpe?g|gif|webp|svg|bmp)$/.test(name);
  }, [file.type, file.name, file.rawFile]);

  useEffect(() => {
    if (!isImage || file.url || !file.rawFile) return;
    const url = URL.createObjectURL(file.rawFile);
    setBlobUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [isImage, file.url, file.rawFile]);

  const activeUrl = file.url || blobUrl;

  if (isImage && activeUrl && !imgError) {
    return (
      <div className="group/lead relative size-10 shrink-0 overflow-hidden rounded-md border border-border/80 bg-surface shadow-2xs">
        <img
          src={activeUrl}
          alt={file.name}
          onError={() => setImgError(true)}
          className="size-full object-cover transition-transform duration-200 group-hover/lead:scale-110"
        />
        <div className="absolute inset-0 flex items-center justify-center bg-black/35 opacity-0 transition-opacity duration-150 group-hover/lead:opacity-100">
          <Eye className="size-3.5 text-white drop-shadow-xs" aria-hidden />
        </div>
      </div>
    );
  }

  return (
    <div className="flex size-10 shrink-0 items-center justify-center rounded-md border border-border/60 bg-muted/20">
      {fileTypeIcon(file.name)}
    </div>
  );
}

/**
 * FileUpload Component - Modern Drag & Dropzone for ERP documents, attachments, and data files.
 */
export function FileUpload({
  files = [],
  onChange,
  onAddFiles,
  onRemoveFile,
  onFileClick,
  visibleRows,
  accept,
  multiple = true,
  maxSizeBytes = 25 * 1024 * 1024, // 25 MB default
  maxCount,
  disabled = false,
  className,
  title = "Kéo thả tài liệu vào đây hoặc bấm để chọn tệp",
  hint,
  error: customError,
}: FileUploadProps) {
  const [isDragOver, setIsDragOver] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const displayError = customError || validationError;

  const handleValidateAndAdd = (newFiles: File[]) => {
    setValidationError(null);

    if (maxCount && files.length + newFiles.length > maxCount) {
      setValidationError(`Tối đa chỉ được tải lên ${maxCount} tệp tin.`);
      return;
    }

    const validFiles: File[] = [];
    for (const f of newFiles) {
      if (f.size > maxSizeBytes) {
        setValidationError(
          `Tệp "${f.name}" (${formatFileSize(f.size)}) vượt quá dung lượng cho phép (${formatFileSize(maxSizeBytes)}).`
        );
        return;
      }
      validFiles.push(f);
    }

    if (validFiles.length > 0) {
      if (onAddFiles) {
        onAddFiles(validFiles);
      }

      if (onChange) {
        const addedItems: UploadedFileItem[] = validFiles.map((f) => ({
          id: `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
          name: f.name,
          size: f.size,
          type: f.type,
          status: "success",
          rawFile: f,
        }));
        onChange(multiple ? [...files, ...addedItems] : addedItems);
      }
    }
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (disabled) return;
    setIsDragOver(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    if (disabled) return;

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFiles = Array.from(e.dataTransfer.files);
      handleValidateAndAdd(multiple ? droppedFiles : [droppedFiles[0]!]);
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selected = Array.from(e.target.files);
      handleValidateAndAdd(multiple ? selected : [selected[0]!]);
      e.target.value = "";
    }
  };

  const handleRemove = (item: UploadedFileItem) => {
    if (onRemoveFile) {
      onRemoveFile(item);
    }
    if (onChange) {
      onChange(files.filter((f) => f.id !== item.id));
    }
  };

  return (
    <div className={cn("space-y-3", className)}>
      {/* Hidden native input */}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        disabled={disabled}
        onChange={handleInputChange}
        className="sr-only"
        aria-hidden="true"
      />

      {/* Dropzone Box */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => !disabled && inputRef.current?.click()}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) => {
          if (!disabled && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        className={cn(
          "relative flex flex-col items-center justify-center rounded-control border-2 border-dashed p-6 text-center transition-all select-none",
          isDragOver
            ? "border-primary bg-primary-soft/40 shadow-xs scale-[0.99]"
            : "border-border/80 bg-surface hover:border-primary/60 hover:bg-bg/50",
          disabled && "opacity-50 cursor-not-allowed hover:border-border/80 hover:bg-surface",
          !disabled && "cursor-pointer"
        )}
      >
        <div className="flex size-12 items-center justify-center rounded-full bg-primary-soft text-primary mb-3">
          <UploadCloud className="size-6" />
        </div>

        <p className="m-0 text-body font-semibold text-text">
          {title}
        </p>

        <p className="mt-1 mb-0 text-caption text-text-muted">
          {hint || `Hỗ trợ tải lên ${multiple ? "nhiều tệp" : "1 tệp"}, tối đa ${formatFileSize(maxSizeBytes)}/tệp.`}
        </p>

        <Button
          type="button"
          size="sm"
          variant="secondary"
          className="mt-3 pointer-events-none"
          tabIndex={-1}
        >
          Chọn tệp từ thiết bị
        </Button>
      </div>

      {/* Error message */}
      {displayError && (
        <div className="flex items-center gap-1.5 text-caption font-semibold text-danger">
          <AlertCircle className="size-4 shrink-0" />
          <span>{displayError}</span>
        </div>
      )}

      {/* Uploaded File List */}
      {files.length > 0 && (
        <ul
          className={cn(
            "m-0 list-none overflow-y-auto overscroll-contain p-0 scrollbar-thin divide-y divide-border/60 rounded-control border border-border/80 bg-surface",
          )}
          style={visibleRows ? { maxHeight: `${visibleRows * 3.5}rem` } : undefined}
        >
          {files.map((file) => (
            <li
              key={file.id}
              className={cn(
                "flex items-center justify-between gap-3 px-3 text-caption transition-colors hover:bg-bg/40",
                visibleRows ? "h-14 shrink-0" : "py-3",
              )}
            >
              {onFileClick ? (
                <button
                  type="button"
                  className="group flex min-w-0 flex-1 cursor-pointer items-center gap-3 border-0 bg-transparent p-0 text-left"
                  onClick={() => onFileClick(file)}
                >
                  <UploadedFileLead file={file} />
                  <span className="min-w-0">
                    <span className="block truncate font-semibold text-text transition-colors group-hover:text-primary">
                      {file.name}
                    </span>
                    <span className="block text-[11px] text-text-muted">
                      {formatFileSize(file.size)}
                      {file.status === "uploading" && ` · Đang tải lên ${file.progress ?? 0}%`}
                    </span>
                  </span>
                </button>
              ) : (
                <div className="flex min-w-0 items-center gap-3">
                  <UploadedFileLead file={file} />
                  <div className="min-w-0">
                    <p className="m-0 truncate font-semibold text-text">
                      {file.name}
                    </p>
                    <p className="m-0 text-[11px] text-text-muted">
                      {formatFileSize(file.size)}
                      {file.status === "uploading" && ` · Đang tải lên ${file.progress ?? 0}%`}
                    </p>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-2 shrink-0">
                {file.status === "success" && (
                  <CheckCircle2 className="size-4 text-success" />
                )}
                {file.status === "uploading" && (
                  <div className="h-1.5 w-16 overflow-hidden rounded-full bg-border">
                    <div
                      className="h-full bg-primary transition-all duration-200"
                      style={{ width: `${file.progress || 30}%` }}
                    />
                  </div>
                )}
                {!disabled && (
                  <IconButton
                    icon={<Trash2 className="size-4" />}
                    size="sm"
                    variant="danger"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemove(file);
                    }}
                    label={`Xóa tệp ${file.name}`}
                  />
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
